import { useEffect, useRef, useCallback } from 'react'
import type Map from 'ol/Map'
import Draw from 'ol/interaction/Draw'
import { createBox } from 'ol/interaction/Draw'
import VectorSource from 'ol/source/Vector'
import type { DrawEvent } from 'ol/interaction/Draw'
import type { Geometry } from 'ol/geom'
import Polygon from 'ol/geom/Polygon'
import type { Coordinate } from 'ol/coordinate'
import type { GeometryFunction } from 'ol/interaction/Draw'
import { useGeometryStore } from '../store/geometryStore'
import { olGeometryToGeoJson, getCentroid, getAreaKm2, getBbox } from '../utils/geo/geojson'
import type { DrawMode, GeoJsonGeometry } from '../types/geo'

const OL_DRAW_TYPE_MAP: Record<string, 'Point' | 'LineString' | 'Polygon' | 'Circle'> = {
  point: 'Point',
  linestring: 'LineString',
  polygon: 'Polygon',
  bbox: 'Circle',
  ellipse: 'LineString', // 3-click: center → major axis end → minor axis end
}

/**
 * 3-click ellipse geometry function (ArcGIS / military GIS style):
 *   Click 1 — center
 *   Click 2 — major axis endpoint (defines semi-major length + azimuth)
 *   Click 3 — minor axis endpoint (perpendicular distance sets semi-minor)
 *
 * During phase 1 (2 coords: center + live mouse), a circle preview is shown
 * so the user can gauge the semi-major radius before committing.
 * During phase 2 (3 coords), the rotated ellipse is drawn live.
 */
function createThreePointEllipse(segments = 64): GeometryFunction {
  return (coordinates, geometry) => {
    const coords = coordinates as Coordinate[]
    const center = coords[0]

    if (coords.length < 2) {
      const ring: Coordinate[] = [center, center]
      if (!geometry) return new Polygon([ring])
      ;(geometry as Polygon).setCoordinates([ring])
      return geometry
    }

    const majorEnd = coords[1]
    const dx = majorEnd[0] - center[0]
    const dy = majorEnd[1] - center[1]
    const a = Math.sqrt(dx * dx + dy * dy) // semi-major radius
    const theta = Math.atan2(dy, dx)        // rotation angle (radians from east)

    let b: number
    if (coords.length >= 3) {
      // Phase 2: project the minor-axis click onto the perpendicular direction
      const mp = coords[2]
      const mpx = mp[0] - center[0]
      const mpy = mp[1] - center[1]
      b = Math.abs(-mpx * Math.sin(theta) + mpy * Math.cos(theta))
    } else {
      // Phase 1 preview: show a circle (semi-minor = semi-major)
      b = a
    }

    if (b < 1e-10) b = 1e-10

    const ring: Coordinate[] = []
    for (let i = 0; i <= segments; i++) {
      const t = (2 * Math.PI * i) / segments
      const ex = center[0] + a * Math.cos(t) * Math.cos(theta) - b * Math.sin(t) * Math.sin(theta)
      const ey = center[1] + a * Math.cos(t) * Math.sin(theta) + b * Math.sin(t) * Math.cos(theta)
      ring.push([ex, ey])
    }

    if (!geometry) return new Polygon([ring])
    ;(geometry as Polygon).setCoordinates([ring])
    return geometry
  }
}

function isLonLatPair(value: unknown): value is [number, number] {
  return (
    Array.isArray(value) &&
    value.length >= 2 &&
    typeof value[0] === 'number' &&
    typeof value[1] === 'number'
  )
}

function normalizeGeoJsonForMode(mode: DrawMode, geojson: GeoJsonGeometry): GeoJsonGeometry {
  if (mode !== 'linestring' || geojson.type === 'LineString') return geojson

  if (geojson.type === 'Polygon' && Array.isArray(geojson.coordinates)) {
    const outerRing = geojson.coordinates[0]
    if (!Array.isArray(outerRing) || outerRing.length < 2) return geojson

    const lineCoords = outerRing.filter(isLonLatPair) as Array<[number, number]>
    if (lineCoords.length < 2) return geojson

    const first = lineCoords[0]
    const last = lineCoords[lineCoords.length - 1]
    const closed =
      first[0] === last[0] &&
      first[1] === last[1]

    return {
      type: 'LineString',
      coordinates: closed ? lineCoords.slice(0, -1) : lineCoords,
    }
  }

  return geojson
}

export function useGeometry(map: Map | null, drawSource: VectorSource) {
  const drawRef = useRef<Draw | null>(null)
  const { setDrawnGeometry, setIsDrawing, openModal, interactionMode, setInteractionMode } =
    useGeometryStore()

  const stopDrawing = useCallback(() => {
    if (!map || !drawRef.current) return
    map.removeInteraction(drawRef.current)
    drawRef.current = null
    setIsDrawing(false)
  }, [map, setIsDrawing])

  const startDrawing = useCallback(
    (mode: DrawMode) => {
      if (!map) return
      stopDrawing()
      drawSource.clear()

      if (mode === 'none') return

      const olType = OL_DRAW_TYPE_MAP[mode]
      if (!olType) return

      const draw = new Draw({
        source: drawSource,
        type: olType,
        geometryFunction:
          mode === 'bbox'
            ? createBox()
            : mode === 'ellipse'
              ? createThreePointEllipse()
              : undefined,
        maxPoints: mode === 'ellipse' ? 3 : undefined,
        freehand: false,
      })

      draw.on('drawstart', () => setIsDrawing(true))

      draw.on('drawend', (evt: DrawEvent) => {
        let geom: Geometry = evt.feature.getGeometry()!

        const geojson = olGeometryToGeoJson(geom)
        if (!geojson) return
        const normalizedGeojson = normalizeGeoJsonForMode(mode, geojson as GeoJsonGeometry)

        const centroid = getCentroid(normalizedGeojson)
        const areaKm2 =
          normalizedGeojson.type === 'Polygon' || normalizedGeojson.type === 'MultiPolygon'
            ? getAreaKm2(normalizedGeojson)
            : undefined
        const bbox =
          normalizedGeojson.type !== 'Point' ? getBbox(normalizedGeojson) : undefined

        setDrawnGeometry({
          mode,
          geojson: normalizedGeojson,
          centroid,
          areaKm2,
          bbox,
        })

        stopDrawing()
        setInteractionMode('none')
        openModal()
      })

      map.addInteraction(draw)
      drawRef.current = draw
    },
    [map, drawSource, stopDrawing, setIsDrawing, setDrawnGeometry, setInteractionMode, openModal]
  )

  // React to mode changes from the store
  useEffect(() => {
    if (interactionMode === 'none') {
      stopDrawing()
    } else {
      startDrawing(interactionMode)
    }
  }, [interactionMode, startDrawing, stopDrawing])

  // Keyboard shortcuts
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        stopDrawing()
        setInteractionMode('none')
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [stopDrawing, setInteractionMode])

  return { startDrawing, stopDrawing }
}
